type PlainObject = Record<string, any>;

function isPlainObject(x: any): x is PlainObject {
  return x !== null && typeof x === "object" && !Array.isArray(x);
}

export function merge<T extends PlainObject, U extends PlainObject>(target: T, source: U): T & U {
  const result: any = { ...target };

  for (const key of Object.keys(source)) {
    const sVal = source[key];
    const tVal = result[key];

    if (isPlainObject(tVal) && isPlainObject(sVal)) {
      result[key] = merge(tVal, sVal);
    } else if (Array.isArray(tVal) && Array.isArray(sVal)) {
      result[key] = [...tVal, ...sVal];
    } else {
      result[key] = sVal;
    }
  }

  return result;
}

export function flattenObject(
  obj: Record<string, any>,
  prefix: string = "",
  result: Record<string, any> = {},
): Record<string, any> {
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      // Construct the new key: current prefix + current key (separated by a dot)
      const newKey = prefix ? `${prefix}.${key}` : key;

      // Check if the value is an object and not null or an array
      // Arrays are treated as final values in this implementation.
      if (typeof value === "object" && value !== null && !Array.isArray(value)) {
        // If it's a nested object, recurse with the newKey as the prefix
        flattenObject(value, newKey, result);
      } else {
        // If it's a primitive value or an array, assign it to the result object
        result[newKey] = value;
      }
    }
  }

  return result;
}

export function safeRead(obj: unknown, path: string) {
  return path.split(".").reduce((acc, key) => {
    if (acc && typeof acc === "object" && key in acc) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}
